import React from 'react';
import { MessageCircle, Heart } from 'lucide-react';
import { ZapPreviewStep } from '../zapGoalTemplates';
import { ImageWithFallback } from '../figma/ImageWithFallback';

/**
 * The conversation this Zap will have, drawn as it will look.
 *
 * It sits under every question and updates as the creator types, so nobody has
 * to be told what an "opener card" or a "follow gate" is — they watch one being
 * built. Steps come from buildPreviewSteps(), which derives them from the rule
 * that will actually be saved, so the picture cannot drift from the behaviour.
 */

interface ZapPreviewProps {
  steps: ZapPreviewStep[];
  /** One line about who ends up receiving it. */
  audienceNote?: string;
  /** Tighter styling when it sits under a question rather than on the recap. */
  compact?: boolean;
  /** Overrides the header — used to name which response is being previewed. */
  title?: string;
}

export function ZapPreview({ steps, audienceNote, compact, title }: ZapPreviewProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-[#fafafa] p-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        <MessageCircle className="h-3 w-3" />
        {title || 'What your follower sees'}
      </p>

      <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
        {steps.map((step, i) => {
          if (step.kind === 'comment') {
            return (
              <div key={i} className="flex items-start gap-2">
                <div className="mt-[2px] flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-300 text-[10px] font-semibold text-white">
                  A
                </div>
                <div className="min-w-0">
                  <p className="mb-0 text-[10px] text-gray-400">They comment on your post</p>
                  <p className="mb-0 text-[13px] font-semibold text-[#111]">{step.text}</p>
                </div>
                <Heart className="ml-auto mt-1 h-3 w-3 flex-shrink-0 text-gray-300" />
              </div>
            );
          }

          if (step.kind === 'reply') {
            return (
              <div key={i} className="ml-8">
                <p className="mb-0 text-[10px] text-gray-400">You reply, where everyone can see</p>
                <p className="mb-0 break-words text-[13px] text-gray-700">{step.text}</p>
              </div>
            );
          }

          if (step.kind === 'button') {
            return (
              <div key={i} className="ml-8 max-w-[85%]">
                <div className="rounded-lg border border-[#e4e4ea] bg-white py-1.5 text-center text-[12px] font-semibold text-[#0095f6]">
                  {step.text}
                </div>
              </div>
            );
          }

          if (step.kind === 'carousel') {
            /* Drawn as the generic template Instagram actually delivers: a
               horizontally scrolling strip of cards, each with its own buttons
               underneath. */
            return (
              <div key={i} className="ml-8">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {step.cards.map((card, ci) => (
                    <div
                      key={ci}
                      className="w-[150px] flex-shrink-0 overflow-hidden rounded-xl border border-[#e4e4ea] bg-white"
                    >
                      <div className="flex h-[96px] items-center justify-center bg-[#f2f2f5]">
                        {card.image ? (
                          <ImageWithFallback
                            src={card.image}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-[10px] text-gray-400">Product photo</span>
                        )}
                      </div>
                      <div className="px-2 pt-1.5">
                        <p className="mb-0 line-clamp-2 text-[11px] font-semibold leading-tight text-[#111]">
                          {card.title}
                        </p>
                        {!!card.subtitle && (
                          <p className="mb-0 truncate text-[10px] text-gray-400">{card.subtitle}</p>
                        )}
                      </div>
                      <div className="p-1.5">
                        {card.buttons.map((label, bi) => (
                          <div
                            key={bi}
                            className="mb-1 rounded-md bg-[#efeff4] py-1 text-center text-[10px] font-semibold text-[#111] last:mb-0"
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {step.cards.length > 1 && (
                  <p className="mb-0 mt-1 text-[10px] text-gray-400">
                    {step.cards.length} cards — they swipe across
                  </p>
                )}
              </div>
            );
          }

          if (step.kind === 'form') {
            /* What opens when they tap the button — the questions they will be
               asked, so the creator sees the whole journey and not just the DM
               that starts it. */
            return (
              <div key={i} className="ml-8 max-w-[85%]">
                <div className="rounded-xl border border-[#e4e4ea] bg-white p-2.5">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Opens your form
                  </p>
                  <p className={`mb-${step.description ? '0.5' : '1.5'} text-[12px] font-semibold text-[#111]`}>
                    {step.title}
                  </p>
                  {!!step.description && (
                    <p className="mb-1.5 text-[10px] leading-snug text-gray-500">{step.description}</p>
                  )}
                  {step.fields.map((label, fi) => (
                    <div key={fi} className="mb-1 rounded-md bg-[#f5f5f7] px-2 py-1.5 last:mb-0">
                      <p className="mb-0 text-[10px] text-gray-500">{label}</p>
                    </div>
                  ))}
                  {!step.fields.length && (
                    <p className="mb-0 text-[10px] text-gray-400">No questions yet</p>
                  )}
                </div>
              </div>
            );
          }

          // dm
          return (
            <div key={i} className="ml-8 max-w-[85%]">
              <div className="rounded-2xl rounded-bl-md bg-[#efeff4] px-3 py-2">
                <p className="mb-0 whitespace-pre-wrap break-words text-[13px] text-[#111]">
                  {step.text || <span className="text-gray-400">Your message…</span>}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {!!audienceNote && (
        <p className="mb-0 mt-2 border-t border-gray-200 pt-2 text-[11px] text-gray-500">
          {audienceNote}
        </p>
      )}
    </div>
  );
}

export default ZapPreview;
